import React, { useState } from 'react';
import styled from 'styled-components';
import { useUser } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import AdminNavbar from '../../components/layout/AdminNavbar';
import CourseManagement from '../../components/admin/CourseManagement';
import CourseEdit from '../../components/admin/CourseEdit';
import BookingManagement from '../../components/admin/BookingManagement';
import UserSettings from '../../components/admin/UserSettings';
import UserManagement from '../../components/admin/UserManagementNew';
import Statistics from '../../components/admin/Statistics';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding-top: 100px; /* Platz für AdminNavbar */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const AdminContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
`;

const AdminHeader = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const AdminTitle = styled.h1`
  font-size: 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 1rem 0;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const AdminSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.2rem;
  margin: 0 0 2rem 0;
  font-weight: 500;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 2rem;
`;

const DashboardCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.3);
  }
  
  h3 {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 0 1rem 0;
    font-size: 1.6rem;
    font-weight: 700;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    margin: 0 0 2rem 0;
    font-size: 1rem;
    line-height: 1.6;
    font-weight: 500;
  }
`;

const ComingSoonBadge = styled.div`
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
  box-shadow: 0 4px 15px rgba(243, 156, 18, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const AdminDashboard = () => {
  const { user } = useUser();
  const [currentView, setCurrentView] = useState('dashboard');
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const handleCourseEdit = (courseId) => {
    setEditingCourse(courseId);
    setCurrentView('course-edit');
  };

  const handleCourseCreate = () => {
    setEditingCourse(null);
    setCurrentView('course-edit');
  };

  const handleBackToCourses = () => {
    setCurrentView('courses');
    setEditingCourse(null);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setEditingCourse(null);
  };

  const handleViewBookings = (courseId) => {
    setCurrentView('bookings');
    // Setze den Kurs-Filter für die Buchungsverwaltung
    setSelectedCourseId(courseId);
  };

  const handleBookingManagement = () => {
    setCurrentView('bookings');
    setSelectedCourseId(null); // Kein spezifischer Kurs-Filter
  };

  const handleUserManagement = () => {
    setCurrentView('user-management');
  };

  const handleStatistics = () => {
    setCurrentView('statistics');
  };

  // Render different views based on current state
  if (currentView === 'courses') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
                      <CourseManagement 
                        onEditCourse={handleCourseEdit}
                        onCreateCourse={handleCourseCreate}
                        onBack={handleBackToDashboard}
                        onViewBookings={handleViewBookings}
                      />
        </AdminContent>
      </AdminContainer>
    );
  }

  if (currentView === 'course-edit') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
          <CourseEdit 
            courseId={editingCourse}
            onBack={handleBackToCourses}
            onSave={handleBackToCourses}
          />
        </AdminContent>
      </AdminContainer>
    );
  }

  if (currentView === 'bookings') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
                      <BookingManagement 
                        onBack={handleBackToDashboard}
                        initialCourseFilter={selectedCourseId}
                      />
        </AdminContent>
      </AdminContainer>
    );
  }

  if (currentView === 'user-settings') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
          <UserSettings onBack={handleBackToDashboard} />
        </AdminContent>
      </AdminContainer>
    );
  }

  if (currentView === 'user-management') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
          <UserManagement onBack={handleBackToDashboard} />
        </AdminContent>
      </AdminContainer>
    );
  }

  if (currentView === 'statistics') {
    return (
      <AdminContainer>
        <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
        <AdminContent>
          <Statistics onBack={handleBackToDashboard} />
        </AdminContent>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminNavbar currentView={currentView} onViewChange={setCurrentView} />
      <AdminContent>
        <AdminHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <AdminTitle>Admin Dashboard</AdminTitle>
          <AdminSubtitle>
            Willkommen im Verwaltungsbereich von DerPonystall
          </AdminSubtitle>
        </AdminHeader>

        <DashboardGrid>
                      <DashboardCard
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        whileHover={{ scale: 1.02 }}
                        onClick={handleStatistics}
                        style={{ cursor: 'pointer' }}
                      >
                        <h3>📊 Statistiken</h3>
                        <p>Übersicht über Buchungen, Kurse und Benutzer</p>
                        <ComingSoonBadge style={{ 
                          background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                          color: 'white',
                          margin: '0 auto',
                          display: 'block',
                          textAlign: 'center'
                        }}>Öffnen</ComingSoonBadge>
                      </DashboardCard>


                      <DashboardCard
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setCurrentView('courses')}
                        style={{ cursor: 'pointer' }}
                      >
                        <h3>🎠 Kurse verwalten</h3>
                        <p>Kurse erstellen, bearbeiten und verwalten</p>
                        <ComingSoonBadge style={{ 
                          background: 'linear-gradient(135deg, #2ecc71, #27ae60)', 
                          color: 'white',
                          margin: '0 auto',
                          display: 'block',
                          textAlign: 'center'
                        }}>Öffnen</ComingSoonBadge>
                      </DashboardCard>

                      <DashboardCard
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                        whileHover={{ scale: 1.02 }}
                        onClick={handleBookingManagement}
                        style={{ cursor: 'pointer' }}
                      >
                        <h3>📋 Buchungen verwalten</h3>
                        <p>Buchungen anzeigen, bearbeiten und verwalten</p>
                        <ComingSoonBadge style={{ 
                          background: 'linear-gradient(135deg, #3498db, #2980b9)', 
                          color: 'white',
                          margin: '0 auto',
                          display: 'block',
                          textAlign: 'center'
                        }}>Öffnen</ComingSoonBadge>
                      </DashboardCard>

          <DashboardCard
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
            onClick={handleUserManagement}
            style={{ cursor: 'pointer' }}
          >
            <h3>👥 Benutzer verwalten</h3>
            <p>Benutzerkonten und Berechtigungen verwalten</p>
            <ComingSoonBadge style={{ 
              background: 'linear-gradient(135deg, #e67e22, #d35400)', 
              color: 'white',
              margin: '0 auto',
              display: 'block',
              textAlign: 'center'
            }}>Öffnen</ComingSoonBadge>
          </DashboardCard>

          <DashboardCard
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
            onClick={() => setCurrentView('user-settings')}
            style={{ cursor: 'pointer' }}
          >
            <h3>⚙️ Nutzereinstellungen</h3>
            <p>Account-Einstellungen und Profil verwalten</p>
            <ComingSoonBadge style={{ 
              background: 'linear-gradient(135deg, #9b59b6, #8e44ad)', 
              color: 'white',
              margin: '0 auto',
              display: 'block',
              textAlign: 'center'
            }}>Öffnen</ComingSoonBadge>
          </DashboardCard>

          <DashboardCard
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
          >
            <h3>⚙️ Einstellungen</h3>
            <p>Website-Einstellungen und Konfiguration</p>
            <ComingSoonBadge>Bald verfügbar</ComingSoonBadge>
          </DashboardCard>
        </DashboardGrid>
      </AdminContent>
    </AdminContainer>
  );
};

export default AdminDashboard;
